import { Box, Typography, useTheme } from '@mui/material';
import { useRouter } from 'next/compat/router';
import { ServiceLineHeroButtonCollection, ServiceLineHeroSubHeadline, ServiceLineHeroBodyCopy, ServiceLineHeroHeadline, ContentTypeRichTextContent, TermsAndConditions as TTermsAndConditions, AnalyticsContext, ServiceLineHeroContentListCollection, ServiceLineHeroContentListItem, ServiceLineHeroContentListBelowButtonsCollection, ServiceLineHeroContentListBelowButtonsItem } from '@/types/generated';
import { wrapper, textContainerStyles, ctaButtonWrapperStyles, TACWrapperStyles, contentListWrapperStyles } from './ServiceLineHeroContent.styles';
import OfficeInfo from '../OfficeInfo/OfficeInfo';
import ServiceLineHeroPAO from '../ServiceLineHeroPAO/ServiceLineHeroPAO';
import Accordions, { IAccordions } from '@shared-components/components/Accordions/Accordions';
import ContentfulButton from '@shared-components/components/ContentfulButton';
import ContentTypeRichText from '@shared-components/components/ContentTypeRichText';
import SingleMessage from '@shared-components/components/SingleMessage';
import Table from '@shared-components/components/Table';
import TermsAndConditions from '@shared-components/components/TermsAndConditions';
import { HeadingTag, Maybe, TAGContentAlignment } from '@shared-components/types';
import { customEvent } from '@utils';
import resolveContentfulHeadingTag from '@utils/resolveContentfulHeadingTag';
import { resolveTypographyVariantStyles } from '@utils/resolveTypographyVariantStyles';
export interface IServiceLineHeroContent {
  headline?: Maybe<ServiceLineHeroHeadline>;
  subHeadline?: Maybe<ServiceLineHeroSubHeadline>;
  bodyCopy?: Maybe<ServiceLineHeroBodyCopy>;
  contentListItems?: ServiceLineHeroContentListCollection['items'];
  contentListBelowButtonsItems?: ServiceLineHeroContentListBelowButtonsCollection['items'];
  buttons?: ServiceLineHeroButtonCollection['items'];
  termsConditions?: Maybe<TTermsAndConditions>;
  analyticsContext?: Maybe<AnalyticsContext>;
  parentBackgroundColor?: Maybe<string>;
  isDarkBackground: boolean;
  contentAlign?: Maybe<TAGContentAlignment>;
  eyebrow?: Maybe<string>;
  eyebrowHtag?: Maybe<HeadingTag>;
  showOfficeDetails?: Maybe<boolean>;
  googleMapsBusinessQuery?: Maybe<string>;
  displayOfficeHoursInModal?: Maybe<boolean>;
  officeHoursLabel?: string;
  narrowLayout?: boolean;
}
const analyticsClick = (context: Maybe<AnalyticsContext> | undefined) => {
  if (context) {
    customEvent('tagEvent', context, context.eventName);
  }
};
const ContentListItemByType = (props: {
  contentItem: ServiceLineHeroContentListItem | ServiceLineHeroContentListBelowButtonsItem;
  contentAlign?: Maybe<string>;
  backgroundColor?: Maybe<string>;
}) => {
  const {
    contentItem,
    contentAlign,
    backgroundColor
  } = props;
  const type = contentItem?.__typename;
  if (!type) return null;
  const centerAligned = contentAlign === 'Center';
  switch (type) {
    case 'ContentTypeRichText':
      return <ContentTypeRichText {...contentItem} disableGutters={true} centerAlign={centerAligned} backgroundColor={backgroundColor} />;
    case 'SingleMessage':
      return <SingleMessage {...contentItem} disableGutters={true} contentAlign={contentAlign} backgroundColor={backgroundColor} />;
    case 'Table':
      return <Table {...contentItem} containerSx={{
        paddingX: 0
      }} titleHtag={contentItem.titleHtag as HeadingTag} backgroundColor={backgroundColor} />;
    case 'Accordions':
      return <Accordions {...contentItem as IAccordions} titleAlignment={contentAlign} disableGutters={true} backgroundColor={backgroundColor} />;
    default:
      return null;
  }
};
export default function ServiceLineHeroContent({
  headline,
  eyebrowHtag = 'normal',
  eyebrow,
  subHeadline,
  bodyCopy,
  contentListItems,
  contentListBelowButtonsItems,
  buttons,
  termsConditions,
  isDarkBackground = false,
  contentAlign = 'Left',
  showOfficeDetails = false,
  googleMapsBusinessQuery,
  displayOfficeHoursInModal = false,
  parentBackgroundColor,
  officeHoursLabel,
  narrowLayout = false
}: Readonly<IServiceLineHeroContent>) {
  const theme = useTheme();
  const router = useRouter();
  const isSickAndUrgentPage = router?.asPath.includes('sick-and-urgent') ?? false;
  const centerAligned = contentAlign === 'Center';
  // Resolve text colors based on background
  let titleColor: string;
  let secondaryColor: string;
  let bodyColorProp: {
    color?: string;
  };
  if (isDarkBackground) {
    titleColor = 'text.light';
    secondaryColor = 'text.light';
    bodyColorProp = {
      color: 'text.light'
    };
  } else {
    titleColor = 'text.primary';
    secondaryColor = 'text.secondary';
    bodyColorProp = {};
  }
  return <Box data-testid="section_content_service_line_hero" sx={wrapper(centerAligned, narrowLayout)} data-sentry-element="Box" data-sentry-component="ServiceLineHeroContent" data-sentry-source-file="ServiceLineHeroContent.tsx">
			<Box sx={textContainerStyles(centerAligned)} data-testid={'service_line_hero_content_container'} data-sentry-element="Box" data-sentry-source-file="ServiceLineHeroContent.tsx">
				{eyebrow && <Typography data-testid="service_line_hero_eyebrow_text" variant="overline" component={resolveContentfulHeadingTag(eyebrowHtag as HeadingTag)} color={secondaryColor}>
						{eyebrow}
					</Typography>}
				{headline && <ContentTypeRichText centerAlign={centerAligned} disableGutters disablePaddingOn={['Top', 'Bottom']} contentMaxWidth="100%" backgroundColor="transparent" dataTestId="group_service_line_hero_headline" content={headline as ContentTypeRichTextContent} docProps={{
        margin: 0,
        sx: [resolveTypographyVariantStyles(theme, 'heroMedium'), {
          [theme.breakpoints.up('md')]: {
            ...resolveTypographyVariantStyles(theme, 'heroLarge'),
            color: titleColor
          }
        }, {
          color: titleColor
        }]
      }} />}

				{subHeadline && <ContentTypeRichText centerAlign={centerAligned} disableGutters disablePaddingOn={['Top', 'Bottom']} contentMaxWidth="100%" backgroundColor="transparent" dataTestId="group_service_line_hero_sub_headline" content={subHeadline as ContentTypeRichTextContent} docProps={{
        margin: 0,
        color: secondaryColor,
        variant: 'header3'
      }} />}

				{bodyCopy && <ContentTypeRichText centerAlign={centerAligned} disableGutters disablePaddingOn={['Top', 'Bottom']} contentMaxWidth="100%" dataTestId="group_service_line_hero_body_copy" backgroundColor="transparent" content={bodyCopy as ContentTypeRichTextContent} docProps={{
        variant: 'bodyMediumBook',
        ...bodyColorProp
      }} />}

				{showOfficeDetails && <OfficeInfo googleMapsBusinessQuery={googleMapsBusinessQuery} displayOfficeHoursInModal={displayOfficeHoursInModal} officeHoursLabel={officeHoursLabel} isDarkBackground={isDarkBackground} />}

				{contentListItems && contentListItems?.length > 0 && <Box data-testid="group_service_line_hero_contentListItems" sx={contentListWrapperStyles}>
						{contentListItems.map((item, index) => item ? <ContentListItemByType contentItem={item} key={`${item?.sys?.id || ''}-${index}`} contentAlign={contentAlign} backgroundColor={parentBackgroundColor} /> : null)}
					</Box>}

				{isSickAndUrgentPage ? <Box data-testid="group_service_line_hero_cta_container" sx={ctaButtonWrapperStyles(centerAligned)}>
						<ServiceLineHeroPAO />
					</Box> : buttons && buttons.length > 0 && <Box data-testid="group_service_line_hero_cta_container" sx={ctaButtonWrapperStyles(centerAligned)}>
							{buttons.map(button => button ? <ContentfulButton key={button.sys.id} {...button} dataTestId="service_line_hero_cta" onClick={() => analyticsClick(button.analyticsContext)} /> : null)}
						</Box>}

				{contentListBelowButtonsItems && contentListBelowButtonsItems.length > 0 && <Box data-testid="group_service_line_hero_contentListBelowButtonsItems" sx={{
        ...contentListWrapperStyles,
        display: 'flex',
        flexDirection: 'column',
        alignItems: centerAligned ? 'center' : 'flex-start',
        width: '100%',
        pt: '1.5rem'
      }}>
						{contentListBelowButtonsItems.map((item, index) => item ? <ContentListItemByType contentItem={item} key={`${item?.sys?.id || ''}-${index}`} contentAlign={contentAlign} backgroundColor={'backgroundColor' in item && item.backgroundColor || parentBackgroundColor} /> : null)}
					</Box>}

				{termsConditions && <Box data-testid="group_service_line_hero_toc_container" sx={TACWrapperStyles}>
						<TermsAndConditions {...termsConditions} textColor={isDarkBackground ? 'text.light' : 'text.secondary'} />
					</Box>}
			</Box>
		</Box>;
}